import React, { useEffect, useRef, useState } from 'react';
import PharmacySelectStep from '../intake/PharmacySelectStep';
import { apiService } from '@/services/api';
import { fmtUsd, hasSamePharmacyOption, manufacturerBrand, manufacturerOption, pharmacyStepComplete } from '@/lib/followupQuote';

// §branded-refill §2.1 — "Where should we send your prescription?"
//
// Same pharmacy  → quote.currentPharmacy.pharmacyId (the quote carries the id
//                  only; the name is looked up best-effort through
//                  /beluga/pharmacy-search near the patient's address).
// A different one → the existing PharmacySelectStep, inline, with the parent
//                  owning Continue.
// Manufacturer   → BE #345: NovoCare (Wegovy) / LillyDirect (Zepbound), only
//                  when the quote lists the route + the verified pharmacy.
//                  Selecting it re-quotes on that leg (the parent) and hides the
//                  directory search — the directory has look-alikes, which is
//                  how a NovoCare refill once went to CVS (visit 84562684).
//
// Answer: { choice: 'same' | 'different' | 'manufacturer', pharmacy?: {pharmacyId, storeName, …} }
const RefillPharmacyStep = ({ question, value, onAnswer, onNext, quote, searchDefaults }) => {
  const answer = value || {};
  const sameAvailable = hasSamePharmacyOption(quote);
  const currentId = quote && quote.currentPharmacy ? String(quote.currentPharmacy.pharmacyId) : null;
  const [sameInfo, setSameInfo] = useState(null); // { storeName, address } | null
  // One lookup per mount. A ref, not state: state in this effect's own deps
  // would re-run it and the cleanup would cancel the in-flight lookup.
  const lookedUpRef = useRef(false);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  // Best-effort name for the "same pharmacy" card: search by the patient's
  // city/state/zip and match the id. The search needs at least one of those;
  // with none we show the generic card. Never blocks the step.
  useEffect(() => {
    if (!sameAvailable || lookedUpRef.current) return;
    const { city, state, zip } = searchDefaults || {};
    if (!city && !state && !zip) return; // wait for an address to seed the search
    lookedUpRef.current = true;
    apiService
      .searchPharmacies({ city: city || undefined, state: state || undefined, zip: zip || undefined })
      .then((res) => {
        if (!aliveRef.current) return;
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.pharmacies) ? res.pharmacies : [];
        const hit = list.find((p) => String(p.PharmacyId) === currentId);
        if (hit) {
          setSameInfo({
            storeName: hit.StoreName || '',
            address: [hit.Address1, hit.City, hit.State, hit.ZipCode].filter(Boolean).join(', '),
            phone: hit.PrimaryPhone || '',
          });
          // Carry the name onto the answer so the summary + pharmacySelection can show it.
          if (answer.choice === 'same') onAnswer({ ...answer, pharmacy: { pharmacyId: currentId, storeName: hit.StoreName || '' } });
        }
      })
      .catch(() => { /* cosmetic only */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAvailable, currentId, searchDefaults?.city, searchDefaults?.state, searchDefaults?.zip]);

  const choose = (choice) => {
    if (choice === 'same') {
      onAnswer({ choice: 'same', pharmacy: { pharmacyId: currentId, storeName: sameInfo?.storeName || '' } });
    } else {
      onAnswer({ choice: 'different', pharmacy: answer.choice === 'different' ? answer.pharmacy : undefined });
    }
  };

  const canContinue = pharmacyStepComplete(quote, answer);
  const mfr = manufacturerOption(quote);
  const mfrBrand = manufacturerBrand(quote);
  const feeCents = Number.isFinite(Number(quote?.consultFeeCents)) && Number(quote.consultFeeCents) > 0
    ? Number(quote.consultFeeCents)
    : Number(quote?.totalCents) || 0;

  return (
    <div data-testid={`step-${question.id}`}>
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">{question.question}</h2>
      {question.subtitle && <p className="text-center text-gray-500 mb-6">{question.subtitle}</p>}

      <div className="space-y-3 mb-6">
        {sameAvailable && (
          <button
            type="button"
            onClick={() => choose('same')}
            className={`option-card w-full p-5 rounded-xl text-left transition-all ${answer.choice === 'same' ? 'selected' : ''}`}
            data-testid="refill-pharmacy-same"
          >
            <p className="text-base font-medium text-gray-800">Same pharmacy</p>
            {sameInfo ? (
              <>
                <p className="text-sm text-gray-600">{sameInfo.storeName}</p>
                <p className="text-sm text-gray-500">{sameInfo.address}</p>
                {sameInfo.phone && <p className="text-xs text-gray-400">{sameInfo.phone}</p>}
              </>
            ) : (
              <p className="text-sm text-gray-500">The pharmacy that filled your last prescription.</p>
            )}
          </button>
        )}
        <button
          type="button"
          onClick={() => choose('different')}
          className={`option-card w-full p-5 rounded-xl text-left transition-all ${answer.choice === 'different' ? 'selected' : ''}`}
          data-testid="refill-pharmacy-different"
        >
          <p className="text-base font-medium text-gray-800">A different pharmacy</p>
          <p className="text-sm text-gray-500">Search for a pharmacy near you.</p>
        </button>
        {mfr && (
          <button
            type="button"
            onClick={() => onAnswer({ choice: 'manufacturer' })}
            className={`option-card w-full p-5 rounded-xl text-left transition-all ${answer.choice === 'manufacturer' ? 'selected' : ''}`}
            data-testid="refill-pharmacy-manufacturer"
          >
            <p className="text-base font-medium text-gray-800">Send to {mfr.name}</p>
            <p className="text-sm text-gray-500">
              {mfrBrand ? `${mfrBrand}'s manufacturer pharmacy. ` : 'The manufacturer\'s own pharmacy. '}
              You pay {mfr.name} for the medication; Fitpal charges the {fmtUsd(feeCents)} consult.
            </p>
          </button>
        )}
      </div>

      {answer.choice === 'manufacturer' && mfr && (
        <p className="text-sm text-gray-700 bg-[#f0faf4] border border-[#2d8a5f]/30 rounded-lg p-3 mb-6" data-testid="refill-pharmacy-manufacturer-confirm">
          Your prescription will be sent to {mfr.name}.
        </p>
      )}

      {answer.choice === 'different' && (
        <div className="mb-6" data-testid="refill-pharmacy-search">
          <PharmacySelectStep
            question={{ id: 'refill-pharmacy-search', question: 'Find your pharmacy' }}
            value={answer.pharmacy}
            onAnswer={(p) => onAnswer({ choice: 'different', pharmacy: p })}
            onNext={onNext}
            searchDefaults={searchDefaults}
            hideContinue
          />
        </div>
      )}

      <button
        onClick={onNext}
        disabled={!canContinue}
        className="btn-primary"
        data-testid="continue-button"
      >
        Continue
      </button>
    </div>
  );
};

export default RefillPharmacyStep;
